import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Package, Truck, CheckCircle, MapPin, Users, MessageCircle, PlusCircle, Clock, Brain } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';

export const LandingHero = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  
  // Animation state for the hero demo
  const [demoStep, setDemoStep] = useState(0);
  const [animationPhase, setAnimationPhase] = useState('loading');
  
  const demoSteps = [
    { title: t('landing.hero.demo.steps.0.title'), subtitle: t('landing.hero.demo.steps.0.subtitle'), icon: MessageCircle, color: 'text-blue-500' },
    { title: t('landing.hero.demo.steps.1.title'), subtitle: t('landing.hero.demo.steps.1.subtitle'), icon: PlusCircle, color: 'text-purple-500' },
    { title: t('landing.hero.demo.steps.2.title'), subtitle: t('landing.hero.demo.steps.2.subtitle'), icon: Truck, color: 'text-orange-500' },
    { title: t('landing.hero.demo.steps.3.title'), subtitle: t('landing.hero.demo.steps.3.subtitle'), icon: CheckCircle, color: 'text-green-500' }
  ];
  
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationPhase('updating');
      setTimeout(() => {
        setDemoStep((prev) => (prev + 1) % demoSteps.length);
        setAnimationPhase('complete');
      }, 300);
      setTimeout(() => {
        setAnimationPhase('loading');
      }, 2000);
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);
  
  const handleGetStarted = (userType: 'customer' | 'carrier') => {
    navigate(`/auth?userType=${userType}`);
  };

  return (
    <section className="relative py-12 md:py-20 px-4 bg-gradient-to-br from-background via-background to-muted/30 overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-r from-[hsl(171,78%,60%)]/10 to-[hsl(195,85%,56%)]/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center lg:min-h-[600px]">
          <div className="animate-fade-in space-y-8 text-center lg:text-left lg:flex-1">
            <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground leading-tight">
              {isRTL ? (
                <>
                  <span className="block">إدارة جميع شركات النقل</span>
                  <span className="block whitespace-nowrap mt-4">الخاصة فيك — من مكان واحد</span>
                </>
              ) : (
                <>
                  <span className="block whitespace-nowrap">Manage All Your Carriers</span>
                  <span className="block mt-3">In One Platform</span>
                </>
              )}
              <div className="flex items-center justify-center lg:justify-start mt-4">
                <span className="inline-flex items-center px-3 sm:px-5 py-2.5 bg-gradient-to-r from-green-50 to-emerald-50 text-green-700 text-xs sm:text-sm font-bold rounded-lg border border-green-200 shadow-sm whitespace-nowrap">
                  <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-2" />
                  {isRTL ? "بدون مكالمات. واتساب. او ايميلات — وبدون اي تكاليف اضافية" : "No WhatsApp. No Calls. No Emails — Zero Costs"}
                </span>
              </div>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto lg:mx-0">
              {isRTL ? (
                <>
                  <span className="font-semibold bg-gradient-to-r from-[hsl(171,78%,60%)] via-[hsl(195,85%,56%)] to-[hsl(171,78%,60%)] bg-[length:200%_100%] animate-[gradientSlide_3s_ease-in-out_infinite] bg-clip-text text-transparent px-2 py-1 rounded-md bg-primary/10">
                    أول منصة لوجستية تشاركية بالذكاء الاصطناعي
                  </span>
                  {" — "}
                  منصة شحنات تمكّن العملاء والناقلين من العمل معًا بشكل مباشر وإدارة الشحنات بشكل أسرع، أذكى، وبدون أي تعقيد.
                </>
              ) : (
                <>
                  The {" "}
                  <span className="font-semibold bg-gradient-to-r from-[hsl(171,78%,60%)] via-[hsl(195,85%,56%)] to-[hsl(171,78%,60%)] bg-[length:200%_100%] animate-[gradientSlide_3s_ease-in-out_infinite] bg-clip-text text-transparent px-2 py-1 rounded-md bg-primary/10">
                    #1 AI-Powered Logistics Network
                  </span>{" "}
                  built for customers and carriers to work together and manage shipments faster, smarter, and without the chaos.
                </>
              )}
            </p>
            
            {/* CTA Buttons */}
            <div className={`flex flex-col sm:flex-row gap-4 ${isRTL ? 'justify-end' : 'justify-center lg:justify-start'} pt-4`}>
              <Button size="lg" onClick={() => handleGetStarted('customer')} className="text-lg px-12 py-6 hover-scale bg-gradient-to-r from-[hsl(171,78%,60%)] to-[hsl(195,85%,56%)] hover:from-[hsl(171,78%,55%)] hover:to-[hsl(195,85%,51%)] text-white border-0 w-full sm:w-auto mx-2 sm:mx-0 max-w-[calc(100%-1rem)]">
                <Package className="mr-2 h-6 w-6" />
                {t('landing.hero.cta_button')}
              </Button>
            </div>
          </div>
          
          <div className="animate-fade-in mt-8 lg:mt-0 lg:flex-1 relative hidden md:block">
            <div className="relative w-full sm:max-w-3xl mx-auto">
              {/* Floating background element */}
              <div className="absolute -top-4 -left-4 w-full h-full bg-gradient-to-r from-[hsl(171,78%,60%)]/20 to-[hsl(195,85%,56%)]/20 rounded-3xl blur-xl"></div>
              <div className="absolute inset-0 bg-gradient-to-r from-[hsl(171,78%,60%)] to-[hsl(195,85%,56%)] rounded-2xl blur-2xl opacity-20 animate-pulse"></div>
              <Card className="relative bg-background/95 backdrop-blur-xl border border-border/30 shadow-2xl w-full max-w-sm sm:max-w-xl min-w-[320px] sm:min-w-[480px] min-h-[400px] hover:shadow-3xl transition-all duration-500 hover:scale-[1.02]">
                <CardContent className="p-4 sm:p-6 md:p-10 w-full min-h-[400px] flex flex-col">
                  <div className="space-y-6 w-full">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full transition-all duration-500 ${
                        animationPhase === 'updating' ? 'bg-yellow-500 animate-pulse' : 'bg-[hsl(171,78%,60%)]'
                      }`}></div>
                      <span className="text-muted-foreground">{t('landing.hero.demo.live_demo')}</span>
                    </div>
                    
                    {/* Animated Load Cards */}
                    <div className="space-y-3 min-h-[240px]">
                      {demoSteps.map((step, index) => {
                        const Icon = step.icon;
                        const isActive = index === demoStep;
                        const isPast = index < demoStep;
                        
                        return (
                          <div
                            key={index}
                            className={`flex items-center gap-3 p-4 rounded-lg border transition-all duration-500 ${
                              isActive
                                ? 'bg-primary/10 border-primary/30 scale-105'
                                : isPast
                                ? 'bg-green-50 border-green-200 opacity-70'
                                : 'bg-muted/30 border-border/50 opacity-40'
                            }`}
                          >
                            <Icon className={`h-5 w-5 transition-all duration-500 ${
                              isActive ? step.color : isPast ? 'text-green-500' : 'text-muted-foreground'
                            }`} />
                            <div className="flex-1">
                              <div className={`font-semibold text-sm transition-all duration-500 ${
                                isActive ? 'text-foreground' : 'text-muted-foreground'
                              }`}>
                                {step.title}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {step.subtitle}
                              </div>
                            </div>
                            {isActive && (
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 bg-current rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-current rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              </div>
                            )}
                            {isPast && <CheckCircle className="h-4 w-4 text-green-500" />}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Progress Bar */}
                    <div className="pt-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                        <Clock className="h-3 w-3" />
                        <span>{t('landing.hero.demo.processing_ai')}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-[hsl(171,78%,60%)] to-[hsl(195,85%,56%)] h-2 rounded-full transition-all duration-1000 ease-out"
                          style={{ width: `${((demoStep + 1) / demoSteps.length) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};